// src/services/shopApi.js
// Central API service for all shop endpoints

const BASE_URL = process.env.REACT_APP_API_URL || '${process.env.REACT_APP_API_URL || "http://localhost:3000/api"}/shop';

// ─── HTTPS IMAGE FIX ──────────────────────────────────────
export const toHttps = (url) => {
  if (!url || typeof url !== 'string') return url;
  return url.replace(/^http:\/\//i, 'https://');
};

const IMAGE_KEYS = new Set([
  'url','image','imageUrl','logo','logoUrl','thumbnail','thumbnailUrl',
  'photo','photoUrl','avatar','avatarUrl','banner','bannerUrl','coverImage','src',
]);

const cleanImageUrls = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(cleanImageUrls);
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => {
      if (IMAGE_KEYS.has(k) && typeof v === 'string') return [k, toHttps(v)];
      if (typeof v === 'string' && v.startsWith('http://') &&
          /\.(jpg|jpeg|png|gif|webp|svg|avif)(\?|$)/i.test(v)) return [k, toHttps(v)];
      return [k, cleanImageUrls(v)];
    })
  );
};

// ─── SESSION ID ───────────────────────────────────────────
export const getSessionId = () => {
  let id = localStorage.getItem('jt_shop_session');
  if (!id) {
    id = 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('jt_shop_session', id);
  }
  return id;
};

// ─── BASE FETCH ───────────────────────────────────────────
const apiFetch = async (path, options = {}) => {
  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      headers: {
        'Content-Type': 'application/json',
        'x-session-id': getSessionId(),
        ...options.headers,
      },
      ...options,
    });
    const data = await res.json().catch(() => ({ success: false, message: 'Invalid response' }));
    return cleanImageUrls(data);
  } catch (error) {
    console.warn(`⚠️ API unavailable [${path}]:`, error.message);
    return { success: false, message: 'API unavailable', data: null };
  }
};

// ─── COUNTRY CACHE ────────────────────────────────────────
// FIX 1: Use localStorage (not sessionStorage) so country persists across tabs
//        and page refreshes. sessionStorage was cleared on every new tab,
//        causing country=null → GL listing served instead of local.
const COUNTRY_KEY   = 'jt_country';
const COUNTRY_TTL   = 24 * 60 * 60 * 1000; // 24 hours

const getGeo = () => {
  try {
    const raw = localStorage.getItem(COUNTRY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Respect TTL — re-detect after 24h in case user travels
    if (Date.now() - (parsed.ts || 0) > COUNTRY_TTL) {
      localStorage.removeItem(COUNTRY_KEY);
      return null;
    }
    return parsed;
  } catch {}
  return null;
};

const saveGeo = (info) => {
  try {
    localStorage.setItem(COUNTRY_KEY, JSON.stringify({ ...info, ts: Date.now() }));
  } catch {}
};

// ─── MARKET FILTER HELPER ────────────────────────────────
// FIX 3 (client-side part): when backend returns both local + GL listings
// for the same product, prefer the local one over GL.
export const filterProductsByMarket = (products, visitorCountry) => {
  if (!products?.length) return products;
  const vc   = visitorCountry?.toUpperCase();
  const seen = new Map(); // masterProductId → product

  for (const p of products) {
    const masterId = p.masterProductId || p.productId || p._id;
    const existing = seen.get(masterId);

    if (!existing) {
      seen.set(masterId, p);
    } else {
      // Local listing always beats GL
      if (p.country === vc && existing.country === 'GL') {
        seen.set(masterId, p);
      }
    }
  }

  return [...seen.values()];
};

// ─── PRODUCTS ─────────────────────────────────────────────
export const shopApi = {

  getFeatured: async (extraParams = {}) => {
    const geo = getGeo() || await detectCountry();
    const q   = new URLSearchParams();
    if (geo?.country) q.set('country', geo.country);
    Object.entries(extraParams).forEach(([k, v]) => { if (v) q.set(k, v); });
    return await apiFetch(`/featured?${q}`);
  },

  // FIX 3: default tab changed from 'all' to 'local'
  // 'all' was returning both local + GL listings for the same product,
  // making the client responsible for dedup — unreliable.
  // 'local' tells the backend: give me exact country + GL fallback,
  // and the backend deduplicates correctly (local beats GL).
  getProducts: async (params = {}) => {
    const q = new URLSearchParams();
    if (!params.country) {
      const geo = getGeo() || await detectCountry();
      if (geo?.country) params.country = geo.country;
    }
    const { currency: _c, globalOnly, localOnly, ...cleanParams } = params;
    if (globalOnly)       cleanParams.tab = 'global';
    else if (localOnly)   cleanParams.tab = 'local';
    else if (!cleanParams.tab) cleanParams.tab = 'all';   // All tab = local+GL combined
    Object.entries(cleanParams).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    return apiFetch(`/products?${q}`);
  },

  // FIX 2: getProduct is now async so it can await detectCountry().
  // Previously it was a sync arrow function — geo was always null on first
  // page load before detectCountry() had run, so no country was sent,
  // backend defaulted to 'US', found no US listing for non-US products,
  // and fell through to GL → wrong price.
  getProduct: async (productId) => {
    const geo = getGeo() || await detectCountry();
    const q   = new URLSearchParams();
    if (geo?.country) q.set('country', geo.country);
    return apiFetch(`/products/${productId}?${q}`);
  },

  search: async (query, page = 1) => {
    const geo = getGeo() || await detectCountry();
    const q   = new URLSearchParams({ q: query, page });
    if (geo?.country) q.set('country', geo.country);
    return apiFetch(`/search?${q}`);
  },

  getCategories: async () => {
    const geo = getGeo() || await detectCountry();
    const q   = geo?.country ? `?country=${geo.country}` : '';
    return apiFetch(`/categories${q}`);
  },

  getIndustries: () => apiFetch('/industries'),

  getBrands: (params = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) q.set(k, v); });
    return apiFetch(`/brands?${q}`);
  },

  getBrand: async (slug) => {
    const geo = getGeo() || await detectCountry();
    const q   = geo?.country ? `?country=${geo.country}` : '';
    return apiFetch(`/brands/${slug}${q}`);
  },

  getProductBrands: (params = {}) => {
    const q = new URLSearchParams({ limit: 8, sort: 'popular', ...params });
    return apiFetch(`/product-brands?${q}`);
  },

  // Cart
  getCart: () => apiFetch('/cart'),

  addToCart: async (id, quantity = 1, variantId = null, country = null, isListingId = false) => {
    const geo = country ? null : (getGeo() || await detectCountry());
    const visitorCountry = country || geo?.country || null;
    const body = isListingId
      ? { listingId: id, quantity, variantId, country: visitorCountry }
      : { productId: id, quantity, variantId, country: visitorCountry };
    return apiFetch('/cart/add', { method: 'POST', body: JSON.stringify(body) });
  },

  updateCartItem: (itemId, quantity) =>
    apiFetch('/cart/update', { method: 'PUT', body: JSON.stringify({ itemId, quantity }) }),
  removeCartItem: (itemId) => apiFetch(`/cart/remove/${itemId}`, { method: 'DELETE' }),
  clearCart:      ()       => apiFetch('/cart/clear', { method: 'DELETE' }),

  // Call this immediately after consumer login — merges guest session cart into user cart.
  // Pass the auth token so the endpoint knows which user to merge into.
  mergeCart: (token) =>
    apiFetch('/cart/merge', {
      method:  'POST',
      headers: { Authorization: `Bearer ${token}` },
    }),

  // Orders
  createOrder: (orderData) =>
    apiFetch('/orders', { method: 'POST', body: JSON.stringify(orderData) }),
  getOrder: (orderId, email) =>
    apiFetch(`/orders/${orderId}${email ? `?email=${encodeURIComponent(email)}` : ''}`),
  getOrderHistory: (email, page = 1) =>
    apiFetch(`/orders/history?email=${encodeURIComponent(email)}&page=${page}`),

  // Payment
  payment: {
    getStatus:          (orderId) => apiFetch(`/payment/status/${orderId}`),
    stripeCreateIntent: (body)    => apiFetch('/payment/stripe/create-intent', { method: 'POST', body: JSON.stringify(body) }),
    stripeConfirm:      (body)    => apiFetch('/payment/stripe/confirm',        { method: 'POST', body: JSON.stringify(body) }),
    paypalCreateOrder:  (body)    => apiFetch('/payment/paypal/create-order',   { method: 'POST', body: JSON.stringify(body) }),
    paypalCapture:      (body)    => apiFetch('/payment/paypal/capture',        { method: 'POST', body: JSON.stringify(body) }),
  },
};

// ─── COUNTRY / CURRENCY DETECTION ────────────────────────
const COUNTRY_NAMES = {
  US:'United States', VN:'Vietnam', SG:'Singapore', JP:'Japan', AU:'Australia',
  GB:'United Kingdom', CA:'Canada', DE:'Germany', FR:'France', KR:'South Korea',
  TH:'Thailand', MY:'Malaysia', ID:'Indonesia', PH:'Philippines', AE:'UAE',
  SA:'Saudi Arabia', CN:'China', NZ:'New Zealand', MX:'Mexico', BR:'Brazil',
};

const CURRENCY_MAP = {
  US:'USD', VN:'VND', SG:'SGD', AU:'AUD', GB:'GBP', CA:'CAD',
  CN:'CNY', JP:'JPY', KR:'KRW', DE:'EUR', FR:'EUR', NZ:'NZD',
  TH:'THB', MY:'MYR', ID:'IDR', PH:'PHP', AE:'AED', SA:'SAR',
  MX:'MXN', BR:'BRL',
};

// Timezone → country (no network needed, instant)
const TIMEZONE_MAP = {
  'Asia/Ho_Chi_Minh':'VN','Asia/Hanoi':'VN','Asia/Saigon':'VN',
  'America/New_York':'US','America/Chicago':'US','America/Denver':'US','America/Los_Angeles':'US',
  'America/Toronto':'CA','America/Vancouver':'CA',
  'Europe/London':'GB','Europe/Paris':'FR','Europe/Berlin':'DE',
  'Asia/Singapore':'SG','Asia/Tokyo':'JP','Asia/Seoul':'KR',
  'Asia/Bangkok':'TH','Asia/Jakarta':'ID','Asia/Kuala_Lumpur':'MY',
  'Asia/Manila':'PH','Asia/Shanghai':'CN','Asia/Hong_Kong':'HK',
  'Asia/Taipei':'TW','Australia/Sydney':'AU','Australia/Melbourne':'AU',
  'Asia/Dubai':'AE','Asia/Riyadh':'SA','America/Sao_Paulo':'BR',
  'America/Mexico_City':'MX','Pacific/Auckland':'NZ','Asia/Kolkata':'IN',
};

export const detectCountry = async () => {
  // Return cached if still valid
  const cached = getGeo();
  if (cached) return cached;

  const normalize = (code, name) => ({
    country:     code,
    countryName: COUNTRY_NAMES[code] || name || code,
    currency:    CURRENCY_MAP[code]  || 'USD',
  });

  const services = [
    async () => {
      const r = await fetch('https://api.country.is/', { signal: AbortSignal.timeout(3000) });
      const d = await r.json();
      if (!d.country) throw new Error('no data');
      return normalize(d.country, d.country);
    },
    async () => {
      const r = await fetch('https://ipinfo.io/json', { signal: AbortSignal.timeout(3000) });
      const d = await r.json();
      if (!d.country) throw new Error('no data');
      return normalize(d.country, d.country);
    },
    async () => {
      const r = await fetch('https://freeipapi.com/api/json', { signal: AbortSignal.timeout(3000) });
      const d = await r.json();
      if (!d.countryCode) throw new Error('no data');
      return normalize(d.countryCode, d.countryName);
    },
  ];

  for (const service of services) {
    try {
      const info = await service();
      saveGeo(info); // FIX 1: save to localStorage with TTL
      return info;
    } catch {}
  }

  // FIX 4: Timezone fallback before giving up — no network needed
  // Old code skipped this and defaulted straight to 'US', which was wrong
  // for non-US users when all IP services failed.
  try {
    const tz   = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const code = TIMEZONE_MAP[tz];
    if (code) {
      const info = normalize(code, COUNTRY_NAMES[code]);
      saveGeo(info);
      return info;
    }
  } catch {}

  // Absolute last resort — GL (global) not US, so GL pricing is shown
  // FIX 4: was 'US' — defaulting to US was wrong for all other visitors
  const fallback = { country: 'GL', countryName: 'Global', currency: 'USD' };
  saveGeo(fallback);
  return fallback;
};

// ─── CURRENCY FORMATTER ───────────────────────────────────
const ZERO_DECIMAL = new Set(['VND','JPY','KRW','IDR','CLP','PYG','BIF','GNF','MGA','RWF','UGX','XAF','XOF']);

export const formatPrice = (amount, currency = 'USD') => {
  if (amount == null || isNaN(Number(amount))) return '';
  const cur    = (currency || 'USD').toUpperCase().trim();
  const isZero = ZERO_DECIMAL.has(cur);
  const value  = Number(amount);

  const locale =
    cur === 'VND' ? 'vi-VN' :
    cur === 'JPY' ? 'ja-JP' :
    cur === 'KRW' ? 'ko-KR' :
    cur === 'IDR' ? 'id-ID' :
    cur === 'EUR' ? 'de-DE' :
    'en-US';

  try {
    return new Intl.NumberFormat(locale, {
      style:                 'currency',
      currency:              cur,
      minimumFractionDigits: isZero ? 0 : 2,
      maximumFractionDigits: isZero ? 0 : 2,
    }).format(value);
  } catch {
    return `${value.toLocaleString()} ${cur}`;
  }
};

export default shopApi;